import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ROUTE_WELCOME, ROUTE_CRMA_REPORT_DATA, ROUTE_CRMA_UPLOAD } from "../../routes/Routes";
import GetFetch from "../../hooks/GetFetch";
import GetFetchZip from '../../hooks/GetFetchZip';
import * as signalR from '@microsoft/signalr';
import DataGrid, {
    Column,
    Editing,
    Item,
    KeyboardNavigation,
    Lookup,
    Pager,
    Paging,
    Scrolling,
    SearchPanel,
    Selection,
    Toolbar
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react";
import CustomPreparedHeaderCell from '../../components/CustomPreparedHeaderCell';
import CustomStore from 'devextreme/data/custom_store';
import { BaseButton } from '../../components/BaseButton/BaseButton';
import { BaseDateField } from '../../components/BaseDateField/BaseDateField';
import { useAuth } from '../../hooks/useAuth';
import { baseUrl } from "../../util/Endpoint";
import { LoadPanel } from 'devextreme-react';
import { useNavigate } from "react-router-dom";
import { allowedPageSizes } from '../../components/GlobalDataGridConfigurations';
import usePageTitle from '../../hooks/usePageTitle';
import './CRMABillingData.scss';
const URL_uploadStatusList = '/v1/CRMABill/GetUploadStatus';

const CRMABillingData = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState();
    const [uploadStatusList, setUploadStatusList] = useState([]);
    const [error, setError] = useState(false);
    const [notification, setNotification] = useState({
        message: '',
        type: undefined,
        visible: false
    });
    const { title } = ROUTE_CRMA_REPORT_DATA;
    const navigate = useNavigate();
    const navigateToHomePage = () => navigate(ROUTE_WELCOME.withSlash);
    const endpoint = '/v1/CRMABill/GetBills';
    const dataGridRef = useRef(null);
    const getAccessToken = () => {
        // Replace 'userToken' with the actual key you use in local storage
        const token = localStorage.getItem('JWT_TOKEN');
        return user ? token : null;
    };
    usePageTitle(title);

    useEffect(() => {
        // const permissions = user.role.ModulePermissions?.filter(module => { return (module.Page == "ROUTE_CONTRACTS_PAGE") })
        // setAllowEditing(permissions?.filter(module => { return (module.Update) }).length > 0)
        // setAllowCreate(permissions?.filter(module => { return (module.Create) }).length > 0 && !unauthorizedCreateRoles.includes(user.role.Name.toLowerCase()))
        fetchCRMABills();
        fetchAndSet(URL_uploadStatusList, setUploadStatusList);
    }, []);

    useEffect(() => {
        // 1. Build the connection instance
        const connection = new signalR.HubConnectionBuilder()
        .withUrl(baseUrl() + `/notifyhub`, { 
            accessTokenFactory: () => getAccessToken()
            //  withCredentials: false 
            })
        .withAutomaticReconnect() // Handles unexpected drops gracefully
        .build();

        // 2. Register your event listeners
        connection.on('ReceiveMessage', (message) => {
            let dataGridDataSource = dataGridRef.current?.instance().getDataSource();
            // Push modification payload directly into the store
            dataGridDataSource.store().push([{
                type: "update",
                key: message.id,
                data: message
            }]);
            dataGridRef.current?.instance().repaintChangesOnly(true);
            dataGridRef.current?.instance().refresh(true);
        });

        // 3. Start the connection
        connection.start()
        .then(() => console.log('SignalR Connected!'))
        .catch((err) => console.error('SignalR Connection Error: ', err));

        // 4. CLEANUP: This runs when the component closes (unmounts)
        return () => {
        if (connection) {
            connection.stop()
            .then(() => console.log('SignalR Connection Gracefully Closed'))
            .catch((err) => console.error('SignalR Stop Error: ', err));
        }};
    }, []); // Empty dependency array ensures this runs once on mount/unmount
    

    const fetchCRMABills = async () => {
        setLoading(true);
        const data = await GetFetch(endpoint);
        const { Success, Message, Errors } = data;
        setError(Success === false);
        if (Success) {
            setReportData(Message);
            // console.log(Message);
        }
        if (Errors?.length > 0) {
            setErrorMessage(Errors[0].Message)
            console.log(Errors)
        }
        setLoading(false);
    };
    
    const fetchAndSet = async (URL, setter) => {
        const { Errors, Message, Success } = await GetFetch(URL);
        if (Success === true) {
            // console.log(Message);
            setter(Message);
        }
        else {
            console.error(Errors[0].Message);
        }
    };
    
    const onBackClick = () => {
        navigateToHomePage();
    };

    const onDownloadClick = async (e) => {
        let result;
        result = await GetFetchZip(`/v1/CRMA/DownloadCRMABillFiles?billPeriod=${e.element.id}`);

        if (result?.Success) {
            setNotification({
                message: 'CRMA Bill Data Successfully Downloaded.',
                type: 'success',
                visible: true
            });
        };

        if (result?.Errors.length > 0) {
            setNotification({
                message: result?.Errors[0].Message ?? 'CRMA Bill Data Not Downloaded.',
                type: 'error',
                visible: true
            });
        };
    };

    const renderDownloadButton = (e) => {
        var item = e.data.id;
        console.log(item);

        return <Button
            id={e.data.BillPeriod}
            visible = {e.data.UploadStatusId === 6}
            icon='floppy'
            hint="Download Bills"
            stylingMode='text'
            onClick={(e) => onDownloadClick(e)}
        />
    }
    const handleBillView = (e) => {
        var billId = e.data.Id
        navigate(ROUTE_CRMA_UPLOAD.withSlash, {
            Id: { billId }
        });
    }
    const renderBillView = (e) => {
        var billId = e.data.Id;
        const label = 'View: ' + billId
        return (
            <a
                href={ROUTE_CRMA_UPLOAD.withSlash + '?id=' + billId} style={{ fontWeight: 'bold', color: '#005288' }}
                onClick={e => handleBillView(e)}>
                {label}
            </a>
        );
    }

    const lowerCaseDisplay = useCallback((rowData) => {
        return rowData.UploadBy ? rowData.UploadBy.toLowerCase() : "";
    }, []);

    const slashDateDisplay = useCallback((rowData) => {
        return rowData.BillPeriod.includes('-') ? rowData.BillPeriod.replace(/-/g, '/') : rowData.BillPeriod;
    }, []);

    return (
        <>
            <div className="block">
                <h1> {title} </h1>
            </div>
            <BaseButton
                id="add_new_crmabill_button_029"
                label={"Add a New CRMA Bill"}
                onClick={() => navigate(ROUTE_CRMA_UPLOAD.withSlash)}
                variant={"outlined"}
            />
            <LoadPanel
                showIndicator
                showPane
                visible={loading}
            />
            <div className="block block__div--wrapper">
                <DataGrid
                    columnAutoWidth={true}
                    dataSource={reportData}
                    highlightChanges={true}
                    onCellPrepared={CustomPreparedHeaderCell}
                    repaintChangesOnly={true}
                    rowAlternationEnabled={true}
                    showBorders={true}
                    showColumnLines={true}
                    showRowLines={true}
                    ref={dataGridRef}
                >
                    <KeyboardNavigation enabled={true} />
                    <SearchPanel
                        highlightCaseSensitive={true}
                        visible={true}
                    />
                    <Selection mode="single" />
                    <Toolbar>
                        <Item
                            name="searchPanel"
                            locateInMenu="auto"
                        />
                        <Item location='after'>
                            <Button text='Home' icon='home' stylingMode='outlined' type='normal' onClick={onBackClick} />
                        </Item>
                    </Toolbar>
                    <Paging defaultPageSize={10} />
                    <Pager
                        allowedPageSizes={allowedPageSizes}
                        showPageSizeSelector={true}
                    />
                    <Column
                        dataField="Id"
                        visible={true}
                        caption="" 
                        width={205} 
                        cellRender={(e) => renderBillView(e)}
                    />
                    <Column
                        caption="BillDate"
                        dataField="BillDate"
                        dataType="date"
                        format="shortDate"
                    />
                    <Column
                        caption="Bill Period"
                        dataField="BillPeriod"
                        dataType="date"
                        format="shortDate"
                    />
                    <Column
                        caption="Last Upload Date"
                        dataField="UploadDate"
                        dataType="date"
                        format="MM/dd/yyyy"
                    />
                    <Column
                        caption="Last Upload User"
                        dataField="UploadBy"
                        calculateDisplayValue={lowerCaseDisplay}
                    />
                    <Column
                        caption="Upload Status"
                        dataField="UploadStatusId">
                        <Lookup
                            dataSource={uploadStatusList}
                            valueExpr="Id"
                            displayExpr="Name" />
                    </Column>
                    <Column
                        dataField="Id"
                        name="Edit"
                        caption="Download"
                        hint="Download Bills"
                        width={100}
                        cellRender={(e) => renderDownloadButton(e)}
                    />
                </DataGrid>
            </div>
            {/* <BaseNotification
                message={notification.message}
                ref={useRef(null)}
                setter={() => setNotification(() => (
                    {
                        isVisible: false,
                        message: '',
                        type: undefined
                    }
                ))}
                type={notification.type}
                visible={notification.visible}
            /> */}
            {
                error &&
                <div className="block block__div--wrapper">
                    An error occurred, which prevented report data was not returned.
                </div>
            }
            {
                notification &&
                <div className="block block__div--wrapper">
                    {notification.message}
                </div>
            }
        </>
    );
};

export default CRMABillingData;